import { ThemeSchemaResponse } from '@api/api/theme-overview';
import {
  type AppTableFeatures,
  appTableFeatures,
  RevampTable,
  TableCell,
  TableHeader,
} from '@design/components/table';
import { ColumnDef, flexRender, useTable } from '@tanstack/react-table';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import clsx from 'clsx';
import { useRef } from 'react';
import styles from './TopCompaniesTable.module.scss';
import {
  ThemeCompanyWithInsights,
  TopCompaniesColumnMeta,
  useTopCompaniesTableColumns,
} from './useTopCompaniesTableColumns';

interface TopCompaniesTableProps {
  data: ThemeCompanyWithInsights[];
  theme: ThemeSchemaResponse;
  isCompact?: boolean;
}

const getColumnMeta = (meta: unknown): TopCompaniesColumnMeta | undefined =>
  meta as TopCompaniesColumnMeta | undefined;

export const TopCompaniesTable = ({
  data,
  theme,
  isCompact = false,
}: TopCompaniesTableProps) => {
  const { setQueryParams } = useQueryParams();
  const { navigateToPage } = useNavigateToAssetOverview();
  const { trackThemeOverviewTopCompaniesRowClicked } = usePosthogEvents();
  const isMacroTheme = theme.type === 'macro';
  const columns = useTopCompaniesTableColumns(isMacroTheme, isCompact);
  const containerRef = useRef<HTMLDivElement>(null);
  const trackingSource = isCompact
    ? POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW_DRAWER
    : POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW;

  const onRowClick = (entityTag: string) => {
    trackThemeOverviewTopCompaniesRowClicked({
      source: trackingSource,
      entities: [entityTag],
      themeIds: [theme.id],
      type: theme.type,
    });

    if (isCompact) {
      navigateToPage({
        entityTag,
        source: trackingSource,
      });
    } else {
      setQueryParams({
        [queryKeys.entityTag]: entityTag,
        [queryKeys.activeDrawer]: queryValues.companyToThemeDrawer,
      });
    }
  };

  const table = useTable({
    features: appTableFeatures,
    data,
    columns: columns as ColumnDef<AppTableFeatures, ThemeCompanyWithInsights>[],
    initialState: {
      pagination: { pageIndex: 0, pageSize: Infinity },
    },
  });

  return (
    <div
      className={clsx(styles.tableWrapper, {
        [styles.isCompact]: isCompact,
      })}
      ref={containerRef}
    >
      <RevampTable className={styles.table} data-testid="top-companies-table">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const meta = getColumnMeta(header.column.columnDef.meta);

                return (
                  <TableHeader
                    key={header.id}
                    className={clsx(styles.tableHeader, meta?.className)}
                    isHoverEnabled={false}
                    alignment={meta?.alignment ?? 'start'}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableHeader>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {!!data?.length &&
            table.getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell, cellIdx) => {
                  const meta = getColumnMeta(cell.column.columnDef.meta);

                  return (
                    <TableCell
                      key={cell.id}
                      className={clsx(styles.tableCell, meta?.className)}
                      alignment={'left'}
                      isNumber={cellIdx === 0}
                      onClick={() => onRowClick(row.original.entity.tag)}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </tr>
            ))}
        </tbody>
      </RevampTable>
    </div>
  );
};
